export const ADMIN_PERMISSIONS = {
    dashboard: 'dashboard',
    products: 'products',
    productSeoPrice: 'productSeoPrice',
    featuresProductItem: 'featuresProductItem',
    categories: 'categories',
    orders: 'orders',
    applications: 'applications',
    users: 'users',
    siteSetting: 'siteSetting',
    analytics: 'analytics',
    gallery: 'gallery',
    billing: 'billing',
    chat: 'chat',
    office: 'office',
    workLog: 'workLog',
    calls: 'calls',
    estimates: 'estimates',
    offers: 'offers',
    mailing: 'mailing',
    materials: 'materials',
    partners: 'partners',
    blog: 'blog',
    portfolio: 'portfolio',
    settingsPages: 'settingsPages',
    productionMaterial: 'productionMaterial',
    colorsProduct: 'colorsProduct',
};

export const DEFAULT_LOCAL_ADMIN_PERMISSIONS = [
    ADMIN_PERMISSIONS.productSeoPrice,
];

export const LOCAL_ADMIN_ACCESS_BY_EMAIL = {
    // Пример, если потом надо дать кому-то больше прав локально:
    // 'naparnik@example.com': [ADMIN_PERMISSIONS.productSeoPrice],
    // 'manager@example.com': [ADMIN_PERMISSIONS.productSeoPrice, ADMIN_PERMISSIONS.orders],
};

const normalizePermissions = (value) => {
    if (!Array.isArray(value)) return [];

    return Array.from(
        new Set(
            value
                .map((item) => String(item || '').trim())
                .filter(Boolean)
        )
    );
};

const normalizePath = (value) => {
    const prepared = String(value || '/').trim();

    if (!prepared) return '/';

    const withoutQuery = prepared.split('?')[0].split('#')[0];

    if (withoutQuery.length > 1) {
        return withoutQuery.replace(/\/+$/, '');
    }

    return withoutQuery || '/';
};

const getUserEmail = (user) => {
    return String(user?.email || '').trim().toLowerCase();
};

export const isSuperAdmin = (user) => {
    return user?.role === 'superAdmin';
};

export const isLocalAdmin = (user) => {
    return user?.role === 'admin';
};

export const getLocalAdminPermissions = (user) => {
    if (isSuperAdmin(user)) return ['*'];

    if (!isLocalAdmin(user)) return [];

    const fromBackend = normalizePermissions(user?.adminPermissions);

    if (fromBackend.length) {
        return fromBackend;
    }

    const email = getUserEmail(user);
    const fromLocalMap = normalizePermissions(LOCAL_ADMIN_ACCESS_BY_EMAIL[email]);

    if (fromLocalMap.length) {
        return fromLocalMap;
    }

    return DEFAULT_LOCAL_ADMIN_PERMISSIONS;
};

export const hasAdminPermission = (user, permission) => {
    if (!permission) return true;
    if (isSuperAdmin(user)) return true;

    const permissions = getLocalAdminPermissions(user);

    return permissions.includes('*') || permissions.includes(permission);
};

export const hasAnyAdminPermission = (user, permissions = []) => {
    const list = normalizePermissions(permissions);

    if (!list.length) return true;
    if (isSuperAdmin(user)) return true;

    const current = getLocalAdminPermissions(user);

    if (current.includes('*')) return true;

    return list.some((permission) => current.includes(permission));
};

const SETTINGS_ROUTES = [
    '/page',
    '/siteCategory',
    '/productListLink',
    '/mainPageSetting',
    '/elementPaymentPageSetting',
    '/regionSetting',
    '/mainImageSetting',
    '/pickupPointsSetting',
    '/warranty',
    '/deliveryInfo',
    '/paymentList',
    '/aboutUsSetting',
    '/benefitsSetting',
    '/contactsSetting',
    '/contactsMSetting',
    '/architectSetting',
    '/architectLiSetting',
    '/architectSkillsSetting',
    '/headerSetting',
    '/costDelivery',
    '/catalog-title',
    '/paymentLI',
    '/test',
];

const ROUTE_RULES = [
    {
        path: '/containerProductPage/product-seo-price',
        exact: true,
        permission: ADMIN_PERMISSIONS.productSeoPrice,
    },
    {
        /*
         * Каталог форм является продолжением раздела «Работы + теги».
         * Поэтому локальный admin с правом productSeoPrice получает доступ
         * и к /admin/catalog-review. superAdmin, как и раньше, проходит всегда.
         */
        path: '/admin/catalog-review',
        exact: true,
        permission: ADMIN_PERMISSIONS.productSeoPrice,
    },
    {
        path: '/products',
        permission: ADMIN_PERMISSIONS.products,
    },
    {
        path: '/featuresProductItem',
        exact: true,
        permission: ADMIN_PERMISSIONS.featuresProductItem,
    },
    {
        path: '/containerProductPage',
        exact: true,
        permissionAny: [
            ADMIN_PERMISSIONS.products,
            ADMIN_PERMISSIONS.productSeoPrice,
            ADMIN_PERMISSIONS.featuresProductItem,
            ADMIN_PERMISSIONS.materials,
            ADMIN_PERMISSIONS.productionMaterial,
            ADMIN_PERMISSIONS.colorsProduct,
        ],
    },
    {
        path: '/containerMaterialPage',
        exact: true,
        permissionAny: [
            ADMIN_PERMISSIONS.materials,
            ADMIN_PERMISSIONS.productionMaterial,
        ],
    },
    {
        path: '/materials',
        exact: true,
        permission: ADMIN_PERMISSIONS.materials,
    },
    {
        path: '/productionMaterial',
        exact: true,
        permission: ADMIN_PERMISSIONS.productionMaterial,
    },
    {
        path: '/colorsProduct',
        exact: true,
        permission: ADMIN_PERMISSIONS.colorsProduct,
    },
    {
        path: '/category',
        exact: true,
        permission: ADMIN_PERMISSIONS.categories,
    },
    {
        path: '/workLog',
        permission: ADMIN_PERMISSIONS.workLog,
    },
    {
        path: '/calls',
        permission: ADMIN_PERMISSIONS.calls,
    },
    {
        path: '/admin/estimates',
        permission: ADMIN_PERMISSIONS.estimates,
    },
    {
        path: '/admin/offers',
        permission: ADMIN_PERMISSIONS.offers,
    },
    {
        path: '/chat',
        permission: ADMIN_PERMISSIONS.chat,
    },
    {
        path: '/officeOwner-setting',
        exact: true,
        permission: ADMIN_PERMISSIONS.office,
    },
    {
        path: '/office-setting',
        exact: true,
        permission: ADMIN_PERMISSIONS.office,
    },
    {
        path: '/mailing',
        permission: ADMIN_PERMISSIONS.mailing,
    },
    {
        path: '/blog',
        permission: ADMIN_PERMISSIONS.blog,
    },
    {
        path: '/orders',
        permission: ADMIN_PERMISSIONS.orders,
    },
    {
        path: '/portfolio',
        permission: ADMIN_PERMISSIONS.portfolio,
    },
    {
        path: '/applicationPage',
        exact: true,
        permission: ADMIN_PERMISSIONS.applications,
    },
    {
        path: '/applications',
        permission: ADMIN_PERMISSIONS.applications,
    },
    {
        path: '/applicationsCallMe',
        exact: true,
        permission: ADMIN_PERMISSIONS.applications,
    },
    {
        path: '/applicationsChangeProfile',
        exact: true,
        permission: ADMIN_PERMISSIONS.applications,
    },
    {
        path: '/applicationsGallery',
        exact: true,
        permission: ADMIN_PERMISSIONS.applications,
    },
    {
        path: '/galleryPhotoPage',
        exact: true,
        permission: ADMIN_PERMISSIONS.gallery,
    },
    {
        path: '/analytics',
        permission: ADMIN_PERMISSIONS.analytics,
    },
    {
        path: '/containerPartnerPage',
        exact: true,
        permission: ADMIN_PERMISSIONS.partners,
    },
    {
        path: '/partner',
        permission: ADMIN_PERMISSIONS.partners,
    },
    {
        path: '/users',
        exact: true,
        permission: ADMIN_PERMISSIONS.users,
    },
    {
        path: '/admin/billing',
        exact: true,
        permission: ADMIN_PERMISSIONS.billing,
    },
    {
        path: '/siteSetting',
        exact: true,
        permission: ADMIN_PERMISSIONS.siteSetting,
    },
    ...SETTINGS_ROUTES.map((path) => ({
        path,
        exact: true,
        permission: ADMIN_PERMISSIONS.settingsPages,
    })),
];

const matchesRule = (pathname, rule) => {
    const current = normalizePath(pathname);
    const target = normalizePath(rule.path);

    if (rule.exact) {
        return current === target;
    }

    return current === target || current.startsWith(`${target}/`);
};

export const getAccessRuleForPath = (pathname) => {
    const current = normalizePath(pathname);

    if (current === '/' || current === '/signin') {
        return null;
    }

    return ROUTE_RULES.find((rule) => matchesRule(current, rule)) || null;
};

export const canAccessRule = (user, rule) => {
    if (!rule) return true;

    if (Array.isArray(rule.permissionAny) && rule.permissionAny.length) {
        return hasAnyAdminPermission(user, rule.permissionAny);
    }

    return hasAdminPermission(user, rule.permission);
};

export const canAccessPath = (user, pathname) => {
    if (isSuperAdmin(user)) return true;

    const current = normalizePath(pathname);

    if (current === '/signin') return true;

    const rule = getAccessRuleForPath(current);

    if (!rule) {
        return false;
    }

    return canAccessRule(user, rule);
};

export const canAccessItem = (user, item) => {
    if (!item) return false;
    if (isSuperAdmin(user)) return true;

    if (Array.isArray(item.permissionAny) && item.permissionAny.length) {
        return hasAnyAdminPermission(user, item.permissionAny);
    }

    if (item.permission) {
        return hasAdminPermission(user, item.permission);
    }

    const href = item.href || item.to || item.path;

    if (!href) return true;

    return canAccessPath(user, href);
};

export const filterAdminItemsByAccess = (items = [], user) => {
    if (!Array.isArray(items)) return [];

    return items.filter((item) => canAccessItem(user, item));
};

export const getDefaultAdminPath = (user) => {
    if (isSuperAdmin(user)) return '/';

    const permissions = getLocalAdminPermissions(user);

    if (permissions.includes('*')) return '/';

    if (permissions.includes(ADMIN_PERMISSIONS.productSeoPrice)) {
        return '/containerProductPage/product-seo-price';
    }

    if (permissions.includes(ADMIN_PERMISSIONS.products)) {
        return '/containerProductPage';
    }

    if (permissions.includes(ADMIN_PERMISSIONS.orders)) {
        return '/orders';
    }

    if (permissions.includes(ADMIN_PERMISSIONS.users)) {
        return '/users';
    }

    if (permissions.includes(ADMIN_PERMISSIONS.chat)) {
        return '/chat';
    }

    return null;
};

export const getAccessDeniedText = (user) => {
    if (!user) {
        return 'Нужно войти в аккаунт.';
    }

    if (isLocalAdmin(user)) {
        return 'Для этого раздела нет локального доступа в админке.';
    }

    return 'Этот раздел доступен только администратору.';
};
